<template>
  <li class="nav-item dropdown mega-menu" @mouseover="$emit('showMenu', menuId)" @mouseleave="$emit('hideMenu')"
    :class="{ 'show': activeMenuId === menuId }">
    <a class="nav-link dropdown-toggle cursor-pointer" role="button" :aria-expanded="activeMenuId === menuId">
      {{ title }}
    </a>
    <div class="dropdown-menu mega-menu-content p-0" :aria-labelledby="`${menuId}Dropdown`">
      <div class="container p-0 m-0">
        <div class="row p-0">
          <div class="col-lg-3 service-parent-layout">
            <ul class="nav flex-column mb-0" role="tablist">
              <template v-if="type === 'grouped'">
                <li class="nav-item" role="presentation" v-for="(items, groupName) in services" :key="groupName">
                  <a class="nav-link mega-menu-link px-3" :class="{ 'active': activeTab === groupName }"
                    @mouseover="selectTab(groupName)">
                    {{ groupName }}
                  </a>
                </li>
              </template>
              <template v-else>
                <li class="nav-item" role="presentation">
                  <a class="nav-link mega-menu-link px-3 active">Divisions</a>
                </li>
              </template>
            </ul>
          </div>

          <div class="col-lg-9">
            <div class="tab-content">
              <template v-if="type === 'grouped'">
                <div class="tab-pane fade" :class="{ 'show active': activeTab === groupName }"
                  v-for="(items, groupName) in services" :key="groupName" role="tabpanel">
                  <div class="mega-menu-submenu">
                    <div class="row">
                      <div class="col-md-6" v-for="(col, index) in servicesInColumns(items)" :key="index">
                        <ul class="list-unstyled mb-0">
                          <li v-for="service in col" :key="service.path">
                            <router-link :to="service.path" class="mega-menu-link">{{ service.displayText
                              }}</router-link>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="tab-pane fade show active" role="tabpanel">
                  <div class="mega-menu-submenu">
                    <div class="row">
                      <div class="col-md-4" v-for="(col, index) in servicesInColumns(services)" :key="index">
                        <ul class="list-unstyled mb-0">
                          <li v-for="service in col" :key="service.path">
                            <router-link :to="service.path" class="mega-menu-link">{{ service.displayText
                              }}</router-link>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </li>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

const props = defineProps({
  title: String,
  menuId: String,
  type: String,
  services: [Object, Array],
  viewAllLink: String,
  activeMenuId: String,
});

defineEmits(['showMenu', 'hideMenu']);

const activeTab = ref('');

watch(() => props.services, (newVal) => {
  if (props.type === 'grouped' && newVal && Object.keys(newVal).length > 0) {
    activeTab.value = Object.keys(newVal)[0];
  }
}, { immediate: true });

const selectTab = (tabName: string) => {
  activeTab.value = tabName;
};

const servicesInColumns = (services: any) => {
  if (!Array.isArray(services)) return [];
  const columns: any[][] = [[], [], []];
  const colSize = Math.ceil(services.length / 3);
  if (colSize === 0) return [];

  services.forEach((service, index) => {
    const colIndex = Math.floor(index / colSize);
    if (columns[colIndex]) {
      columns[colIndex].push(service);
    }
  });
  return columns;
};
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

.nav-item.mega-menu {
  position: static;
}

.mega-menu .dropdown-menu {
  width: 900px;
  display: none;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out, visibility 0.3s;
  visibility: hidden;
  pointer-events: none;
  margin-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 5px 15px rgba(0, 0, 0, 0.1);
  background-color: white;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

@keyframes dropdown-show {
  from {
    opacity: 0;
    transform: translateY(10px) translateX(-50%);
  }

  to {
    opacity: 1;
    transform: translateY(0) translateX(-50%);
  }
}

.nav-item.mega-menu.show>.dropdown-menu {
  display: block;
  visibility: visible;
  opacity: 1;
  pointer-events: auto;
  animation: dropdown-show .2s forwards;
}

.mega-menu-content .row {
  flex-wrap: wrap;
  margin: 0;
}

.service-parent-layout {
  width: fit-content;
  border-right: 1px solid #dee2e6;
  background-color: #f5f5f8;
  padding: 1rem;
  margin-right: 1rem;
}

.service-parent-layout .nav-item {
  width: auto;
}

.service-parent-layout .nav-link {
  border: none;
  border-radius: 0;
  text-align: left;
  padding: 0.5rem 1rem;
  color: var(--bs-dark);
  transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out;
  margin: 0.5rem !important;
  border-radius: 5px;
  cursor: pointer;
}

.service-parent-layout .nav-link.active {
  background-color: var(--bs-dark);
  color: white;
}

.service-parent-layout .nav-link:hover:not(.active) {
  color: var(--bs-primary);
}

.tab-content {
  padding: 1rem;
  flex-grow: 1;
}

.mega-menu-submenu ul {
  list-style: none;
  padding: 0;
}

.mega-menu-submenu li a.mega-menu-link {
  padding: 0.75rem;
  display: block;
  color: #555;
  text-decoration: none;
  transition: color 0.2s ease-in-out;
  font-size: 0.9rem;
}

.mega-menu-submenu li a.mega-menu-link:hover {
  color: var(--bs-secondary);
}

.mega-menu .dropdown-toggle::after {
  border: none;
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  content: "\f078";
  display: inline-block;
  margin-left: 0.5em;
  vertical-align: 0.05em;
  font-size: 0.6em;
  transition: transform 0.2s ease-in-out;
}

.nav-item.dropdown.show .dropdown-toggle::after {
  transform: rotate(180deg);
}
</style>